import React, { useEffect, useState } from 'react';
import useTextStore from '../store/useTextStore';
import useSceneStore from '../store/useSceneStore';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';

const { FiType, FiTrash2, FiCheck, FiX, FiCopy, FiAlignLeft, FiAlignCenter, FiAlignRight, FiBold } = FiIcons;

const TextPanel = () => {
  const { tool } = useSceneStore();
  const { 
    textItems, 
    selectedTextId, 
    isEditing, 
    defaultSettings, 
    updateTextItem, 
    updateDefaultSettings, 
    removeTextItem, 
    duplicateTextItem,
    clearTextSelection 
  } = useTextStore();
  
  const selectedItem = selectedTextId ? textItems[selectedTextId] : null;

  const [localState, setLocalState] = useState({
    text: '',
    fontSize: 16,
    fontFamily: 'Inter',
    color: '#000000',
    rotation: 0,
    fontWeight: 'normal',
    textAlign: 'center'
  });

  useEffect(() => {
    if (selectedItem) {
      setLocalState({
        text: selectedItem.text,
        fontSize: selectedItem.fontSize,
        fontFamily: selectedItem.fontFamily,
        color: selectedItem.color,
        rotation: selectedItem.rotation,
        fontWeight: selectedItem.fontWeight || 'normal',
        textAlign: selectedItem.textAlign || 'center'
      });
    } else {
      setLocalState({
        text: defaultSettings.text,
        fontSize: defaultSettings.fontSize,
        fontFamily: defaultSettings.fontFamily,
        color: defaultSettings.color,
        rotation: 0,
        fontWeight: defaultSettings.fontWeight || 'normal',
        textAlign: defaultSettings.textAlign || 'center'
      });
    }
  }, [selectedTextId, defaultSettings, isEditing]);

  if (!selectedTextId || !isEditing) {
    return null;
  }

  const handleChange = (key, value) => {
    setLocalState(prev => ({ ...prev, [key]: value }));
    if (selectedTextId) {
      updateTextItem(selectedTextId, { [key]: value });
    } else {
      updateDefaultSettings({ [key]: value });
    }
  };

  const handleSizePreset = (size) => { handleChange('fontSize', size); };
  
  const handleRotationChange = (radians) => {
    if (selectedTextId) {
      updateTextItem(selectedTextId, { rotation: radians });
      setLocalState(prev => ({ ...prev, rotation: radians }));
    }
  };
  
  const handleConfirm = () => { clearTextSelection(); };
  
  const handleDuplicate = () => {
    if (selectedTextId) {
      duplicateTextItem(selectedTextId);
    }
  };

  const handleToggleBold = () => {
    const newValue = localState.fontWeight === 'bold' ? 'normal' : 'bold';
    handleChange('fontWeight', newValue);
  };

  const handleKeyDown = (e) => { if (e.ctrlKey && e.key === 'Enter') { handleConfirm(); } };

  const fontOptions = [
    { label: 'Inter (domyślna)', value: 'Inter' },
    { label: 'Arial', value: 'Arial' },
    { label: 'Serif', value: 'serif' },
    { label: 'Monospace', value: 'monospace' },
    { label: 'Comic Sans MS', value: '"Comic Sans MS"' }
  ];

  const sizePresets = [
    { label: 'Mały', value: 12 },
    { label: 'Normalny', value: 16 },
    { label: 'Duży', value: 24 },
    { label: 'Bardzo duży', value: 32 },
    { label: 'Nagłówek', value: 48 }
  ];

  const colorOptions = [ '#000000', '#475569', '#dc2626', '#1E2937', '#16a34a', '#f59e0b' ];

  return (
    <div className="absolute left-20 bottom-4 w-80 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden z-10" style={{ maxHeight: 'calc(100vh - 100px)' }}>
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiType} className="text-[#1E2937] text-lg" />
          <h3 className="font-semibold text-gray-700 text-sm">Edycja tekstu</h3>
        </div>
        <button 
          onClick={() => removeTextItem(selectedTextId)}
          className="p-1.5 hover:bg-red-100 hover:text-red-600 rounded-lg transition-colors"
          title="Usuń tekst"
        >
          <SafeIcon icon={FiTrash2} className="text-sm" />
        </button>
      </div>

      <div className="p-4 space-y-4 overflow-y-auto">
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-500 uppercase">Treść tekstu</label>
          <textarea 
            value={localState.text} 
            onChange={(e) => handleChange('text', e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 min-h-[80px]"
            placeholder="Wpisz tekst..."
          />
        </div>

        <div className="space-y-3">
          <label className="text-xs font-medium text-gray-500 uppercase">Formatowanie</label>
          
          {/* Alignment and Bold Controls */}
          <div className="flex gap-2">
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
               <button 
                  onClick={() => handleChange('textAlign', 'left')}
                  className={clsx("p-2 hover:bg-gray-50", localState.textAlign === 'left' && "bg-gray-200")}
                  title="Wyrównaj do lewej"
               >
                 <SafeIcon icon={FiAlignLeft} className="text-sm" />
               </button>
               <button 
                  onClick={() => handleChange('textAlign', 'center')}
                  className={clsx("p-2 hover:bg-gray-50 border-l border-gray-200", localState.textAlign === 'center' && "bg-gray-200")}
                  title="Wyrównaj do środka"
               >
                 <SafeIcon icon={FiAlignCenter} className="text-sm" />
               </button>
               <button 
                  onClick={() => handleChange('textAlign', 'right')}
                  className={clsx("p-2 hover:bg-gray-50 border-l border-gray-200", localState.textAlign === 'right' && "bg-gray-200")}
                  title="Wyrównaj do prawej"
               >
                 <SafeIcon icon={FiAlignRight} className="text-sm" />
               </button>
            </div>
            
            <button 
               onClick={handleToggleBold}
               className={clsx(
                 "p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors",
                 localState.fontWeight === 'bold' && "bg-gray-200 border-gray-400"
               )}
               title="Pogrubienie"
            >
               <SafeIcon icon={FiBold} className="text-sm" />
            </button>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
              {sizePresets.map(preset => (
                <button 
                  key={preset.label}
                  onClick={() => handleSizePreset(preset.value)}
                  className={clsx(
                    "px-2 py-1 text-xs rounded border transition-colors",
                    localState.fontSize === preset.value ? "bg-gray-200 border-[#1E2937] text-[#1E2937]" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Rozmiar (px):</label>
              <input 
                type="number" 
                value={localState.fontSize} 
                onChange={(e) => handleChange('fontSize', parseInt(e.target.value))}
                className="w-16 px-2 py-1 border border-gray-200 rounded text-sm"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500">Czcionka</label>
            <select 
              value={localState.fontFamily} 
              onChange={(e) => handleChange('fontFamily', e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
            >
              {fontOptions.map(font => (
                <option key={font.value} value={font.value}>{font.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500">Kolor</label>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map(color => (
                <button 
                  key={color} 
                  onClick={() => handleChange('color', color)}
                  className={clsx(
                    "w-6 h-6 rounded-full border relative",
                    localState.color === color ? "ring-2 ring-gray-500 ring-offset-1" : "border-gray-200"
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
              <input 
                type="color" 
                value={localState.color} 
                onChange={(e) => handleChange('color', e.target.value)}
                className="w-6 h-6 p-0 border-0 rounded-full overflow-hidden cursor-pointer"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500">Obrót</label>
            <input 
              type="range" 
              min="0" max="360" 
              value={Math.round(localState.rotation * 180 / Math.PI)} 
              onChange={(e) => handleRotationChange(parseInt(e.target.value) * Math.PI / 180)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t border-gray-100">
          <label className="text-xs font-medium text-gray-500 uppercase">Podgląd</label>
          <div className="w-full h-24 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden p-2">
            <div style={{
              fontSize: `${Math.min(localState.fontSize, 32)}px`,
              fontFamily: localState.fontFamily,
              color: localState.color,
              textAlign: localState.textAlign,
              fontWeight: localState.fontWeight,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.2
            }}>
              {localState.text || 'Podgląd tekstu'}
            </div>
          </div>
        </div>

        <div className="bg-gray-100 p-3 rounded-lg text-xs text-gray-600 space-y-1">
          <div className="font-medium mb-1">Wskazówki:</div>
          <ul className="list-disc list-inside space-y-0.5 opacity-80">
            <li>Użyj Ctrl+Enter aby szybko zapisać</li>
            <li>Tekst można później przesuwać i obracać</li>
            <li>Dwukrotne kliknięcie otwiera edytor</li>
          </ul>
        </div>

        <div className="flex gap-2 pt-2 border-t border-gray-200">
          <button 
            onClick={handleDuplicate}
            className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
          >
            <SafeIcon icon={FiCopy} className="text-sm" />
            Duplikuj
          </button>
          <button 
            onClick={handleConfirm}
            className="flex-1 px-3 py-2 bg-[#1E2937] text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-sm font-medium shadow-sm"
          >
            <SafeIcon icon={FiCheck} className="text-sm" />
            Zatwierdź
          </button>
        </div>
      </div>
    </div>
  );
};

export default TextPanel;